import React, { useEffect, useState } from "react";
import api, { formatApiError, API } from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  Package,
  Truck,
  FilePdf,
  Info,
  ArrowRight,
  CalendarBlank,
  IdentificationCard,
  Plus,
  X,
  ClipboardText,
} from "@phosphor-icons/react";
import { DateTimePicker } from "@/components/DateTimePicker";

const ACCENT = "#10B981"; // green

export default function NewRecoleccion() {
  const nav = useNavigate();
  const [providers, setProviders] = useState([]);
  const [unitTypes, setUnitTypes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);

  const [form, setForm] = useState({
    when: null,
    provider_id: "",
    unit_type_id: "",
    persona_recolecta: "",
    placas: "",
    pedidos: [""],
    notes: "",
  });

  useEffect(() => {
    (async () => {
      const [p, u] = await Promise.all([
        api.get("/catalogs/providers"),
        api.get("/catalogs/unit-types"),
      ]);
      setProviders(p.data);
      setUnitTypes(u.data);
    })();
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setPedido = (i, v) =>
    setForm((f) => ({ ...f, pedidos: f.pedidos.map((p, idx) => (idx === i ? v : p)) }));
  const addPedido = () => setForm((f) => ({ ...f, pedidos: [...f.pedidos, ""] }));
  const removePedido = (i) =>
    setForm((f) => ({ ...f, pedidos: f.pedidos.length === 1 ? [""] : f.pedidos.filter((_, idx) => idx !== i) }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.when || !form.provider_id || !form.unit_type_id) {
      toast.error("Fecha, cliente y tipo de unidad son obligatorios");
      return;
    }
    if (!form.persona_recolecta.trim() || !form.placas.trim()) {
      toast.error("Captura persona que recolecta y placas");
      return;
    }
    const pedidosClean = form.pedidos.map((p) => p.trim()).filter(Boolean);
    if (pedidosClean.length === 0) {
      toast.error("Agrega al menos un pedido o RMA a recolectar");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/recolecciones", {
        appointment_datetime: form.when.toISOString(),
        provider_id: form.provider_id,
        unit_type_id: form.unit_type_id,
        persona_recolecta: form.persona_recolecta,
        placas: form.placas,
        pedidos: pedidosClean,
        notes: form.notes,
      });
      setCreated(data);
      toast.success(`Recolección creada · Folio ${data.folio}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const downloadPdf = async () => {
    if (!created) return;
    try {
      const token = localStorage.getItem("rcn_token");
      const res = await fetch(`${API}/recolecciones/${created.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("No se pudo generar PDF");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Recoleccion_${created.folio}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.message);
    }
  };

  if (created) {
    return (
      <div className="max-w-2xl mx-auto fade-up">
        <div className="font-mono-data text-[11px] uppercase tracking-[0.3em] mb-2" style={{ color: ACCENT }}>
          Recolección · Creada
        </div>
        <h1 className="font-display text-4xl mb-6">Pase de Recolección</h1>

        <div className="bg-[#111111] border border-white/10 rounded-sm p-8 text-center mb-6">
          <div className="text-[10px] font-mono-data uppercase tracking-widest text-white/50 mb-3">
            Folio asignado
          </div>
          <div
            data-testid="rec-created-folio"
            className="font-mono-data font-bold text-6xl mb-2"
            style={{ color: ACCENT }}
          >
            {created.folio}
          </div>
          <div className="text-sm text-white/50">
            {created.cliente_name} · {created.unit_type_name} · {created.pedidos?.length || 0} pedidos
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <button
            data-testid="rec-download-pdf-btn"
            onClick={downloadPdf}
            className="flex items-center justify-center gap-2 px-5 py-3 rounded-sm text-white font-semibold text-sm hover:brightness-110 active:scale-[0.98] transition-transform"
            style={{ backgroundColor: ACCENT }}
          >
            <FilePdf size={18} weight="fill" /> Descargar Pase PDF
          </button>
          <button
            data-testid="rec-goto-detail-btn"
            onClick={() => nav(`/recolecciones/${created.id}`)}
            className="flex items-center justify-center gap-2 px-5 py-3 rounded-sm border border-white/10 text-sm hover:bg-white/5 transition-colors"
          >
            Ir a detalle <ArrowRight size={16} />
          </button>
        </div>

        <button
          onClick={() => {
            setCreated(null);
            setForm({
              when: null, provider_id: "", unit_type_id: "",
              persona_recolecta: "", placas: "", pedidos: [""], notes: "",
            });
          }}
          className="mt-6 text-sm text-white/50 hover:text-white transition-colors"
        >
          + Crear otra recolección
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <div className="font-mono-data text-[11px] uppercase tracking-[0.3em] mb-2" style={{ color: ACCENT }}>
          Nueva · Recolección
        </div>
        <h1 className="font-display text-4xl sm:text-5xl">Programar Recolección</h1>
        <p className="text-white/50 text-sm mt-2">
          Para recolecciones en proveedor o cliente. El folio se asignará automáticamente.
        </p>
      </div>

      <form onSubmit={submit} className="max-w-4xl">
        <div className="bg-[#111111] border border-white/10 rounded-sm p-6">
          <SectionTitle icon={CalendarBlank} title="Fecha y hora de recolección" accent={ACCENT} />
          <div className="mb-6">
            <DateTimePicker
              value={form.when}
              onChange={(v) => update("when", v)}
              testIdDate="rec-input-date"
              testIdTime="rec-input-time"
            />
          </div>

          <SectionTitle icon={Truck} title="Cliente y unidad" accent={ACCENT} />
          <div className="grid sm:grid-cols-2 gap-4 mb-6">
            <SelectField
              label="Cliente / Proveedor"
              value={form.provider_id}
              onChange={(v) => update("provider_id", v)}
              options={providers}
              testId="rec-select-cliente"
              accent={ACCENT}
            />
            <SelectField
              label="Tipo de unidad (en la que recolectan)"
              value={form.unit_type_id}
              onChange={(v) => update("unit_type_id", v)}
              options={unitTypes}
              testId="rec-select-unit"
              accent={ACCENT}
            />
          </div>

          <SectionTitle icon={IdentificationCard} title="Datos del recolector" accent={ACCENT} />
          <div className="grid sm:grid-cols-2 gap-4 mb-6">
            <Input
              label="Persona que recolecta"
              value={form.persona_recolecta}
              onChange={(v) => update("persona_recolecta", v)}
              testId="rec-input-persona"
              accent={ACCENT}
            />
            <Input
              label="Placas de la unidad"
              value={form.placas}
              onChange={(v) => update("placas", v.toUpperCase())}
              testId="rec-input-placas"
              accent={ACCENT}
            />
          </div>

          <SectionTitle icon={ClipboardText} title="Pedidos / RMA a recolectar" accent={ACCENT} />
          <div className="space-y-2 mb-6">
            {form.pedidos.map((p, i) => (
              <div key={i} className="flex gap-2">
                <input
                  data-testid={`rec-input-pedido-${i}`}
                  value={p}
                  onChange={(e) => setPedido(i, e.target.value)}
                  placeholder={`Pedido / RMA #${i + 1}`}
                  className="flex-1 px-3 py-2.5 rounded-sm bg-[#0A0A0A] border border-white/10 text-sm text-white placeholder:text-white/30 font-mono-data outline-none"
                  style={{ caretColor: ACCENT }}
                  onFocus={(e) => (e.target.style.borderColor = ACCENT)}
                  onBlur={(e) => (e.target.style.borderColor = "rgba(255,255,255,0.1)")}
                />
                <button
                  type="button"
                  data-testid={`rec-remove-pedido-${i}`}
                  onClick={() => removePedido(i)}
                  className="px-3 rounded-sm border border-white/10 text-white/50 hover:bg-white/5"
                >
                  <X size={14} />
                </button>
              </div>
            ))}
            <button
              type="button"
              data-testid="rec-add-pedido"
              onClick={addPedido}
              className="flex items-center gap-2 text-xs font-mono-data uppercase tracking-widest hover:brightness-110"
              style={{ color: ACCENT }}
            >
              <Plus size={14} /> Agregar pedido
            </button>
          </div>

          <SectionTitle icon={Info} title="Notas (opcional)" accent={ACCENT} />
          <textarea
            data-testid="rec-input-notes"
            value={form.notes}
            onChange={(e) => update("notes", e.target.value)}
            rows={3}
            placeholder="Instrucciones especiales…"
            className="w-full px-3 py-2.5 rounded-sm bg-[#0A0A0A] border border-white/10 text-sm text-white placeholder:text-white/30 outline-none"
          />
        </div>

        <div className="mt-6 flex items-center gap-3">
          <button
            data-testid="rec-submit"
            disabled={busy}
            className="px-6 py-3 rounded-sm text-white font-semibold text-sm hover:brightness-110 active:scale-[0.98] transition-transform disabled:opacity-50"
            style={{ backgroundColor: ACCENT }}
          >
            {busy ? "Guardando…" : "Crear recolección y generar folio"}
          </button>
          <button
            type="button"
            onClick={() => nav("/recolecciones")}
            className="px-4 py-3 rounded-sm border border-white/10 text-sm text-white/70 hover:bg-white/5 transition-colors"
          >
            Cancelar
          </button>
        </div>
      </form>
    </div>
  );
}

function SectionTitle({ icon: Icon, title, accent }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <Icon size={16} style={{ color: accent }} />
      <div className="font-mono-data text-[10px] uppercase tracking-widest text-white/50">{title}</div>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", testId, accent }) {
  return (
    <div>
      <label className="block text-[11px] font-mono-data uppercase tracking-widest text-white/50 mb-2">
        {label}
      </label>
      <input
        data-testid={testId}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2.5 rounded-sm bg-[#0A0A0A] border border-white/10 text-sm text-white outline-none transition-colors"
        onFocus={(e) => (e.target.style.borderColor = accent)}
        onBlur={(e) => (e.target.style.borderColor = "rgba(255,255,255,0.1)")}
      />
    </div>
  );
}

function SelectField({ label, value, onChange, options, testId, accent }) {
  return (
    <div>
      <label className="block text-[11px] font-mono-data uppercase tracking-widest text-white/50 mb-2">
        {label}
      </label>
      <select
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2.5 rounded-sm bg-[#0A0A0A] border border-white/10 text-sm text-white outline-none"
        onFocus={(e) => (e.target.style.borderColor = accent)}
        onBlur={(e) => (e.target.style.borderColor = "rgba(255,255,255,0.1)")}
      >
        <option value="">Selecciona…</option>
        {options.map((o) => (
          <option key={o.id} value={o.id}>{o.name}</option>
        ))}
      </select>
    </div>
  );
}
